import { useState, useEffect, useMemo } from 'react'
import { useActiveAndCompletedTurns } from '@renderer/chat/store/turn-status'
import { FadingScrollable } from '@renderer/chat'
import { ActiveChatCell } from './active-chat-cell'

const MAX_COLLAPSED_CHATS = 3

// Tall enough for a few more rows than the collapsed cap without the composer
// swallowing the panel — the footer is bottom-anchored, so every pixel here
// pushes the section stack up.
const EXPANDED_MAX_HEIGHT_PX = 160

/**
 * In-flight and just-finished chats, rendered inside the chat input's card
 * (see `ChatInput`'s `header` slot) rather than floating above it.
 *
 * They used to be free-floating pills in the footer, which left them flush
 * against the panel's edges while every other row was inset. Sitting inside the
 * composer they read as part of it — the thing you just sent, still working.
 */
export function ActiveChats() {
  const chats = useActiveAndCompletedTurns()
  const [isExpanded, setIsExpanded] = useState(false)

  const hasChats = chats.length > 0
  const hasMore = chats.length > MAX_COLLAPSED_CHATS

  useEffect(() => {
    if (!hasMore) setIsExpanded(false)
  }, [hasMore])

  const sortedChats = useMemo(
    () =>
      [...chats].sort((a, b) => {
        const aTime = a.completedAt ?? a.startTime ?? 0
        const bTime = b.completedAt ?? b.startTime ?? 0
        return bTime - aTime
      }),
    [chats],
  )

  const displayedChats = useMemo(
    () => (isExpanded ? sortedChats : sortedChats.slice(0, MAX_COLLAPSED_CHATS)),
    [isExpanded, sortedChats],
  )

  if (!hasChats) {
    return null
  }

  const rows = displayedChats.map((chat) => <ActiveChatCell key={chat.entryId} chat={chat} />)

  return (
    // `p-1` is load-bearing, not just breathing room: the first row's hover fill
    // sits in the chat input card's top corners, so it has to be inset far
    // enough to clear their curve. 4px inside a 16px (`rounded-2xl`) card makes
    // `rounded-xl` on the rows exactly concentric with it. The hairline keeps
    // the rows reading as their own register above the context chips.
    <div className="flex flex-col border-b p-1">
      {isExpanded ? (
        <FadingScrollable fadeSize="3%" className="flex flex-col" style={{ maxHeight: EXPANDED_MAX_HEIGHT_PX }}>
          {rows}
        </FadingScrollable>
      ) : (
        rows
      )}

      {hasMore && (
        <button
          onClick={() => setIsExpanded((v) => !v)}
          // Same `px-1.5 py-1.5` as the rows: the fill is full-width like
          // theirs, so a different inset would step the label off the status
          // dots above it and shorten the box out of the list's rhythm.
          className="text-foreground-secondary hover:text-foreground hover:bg-element-hover corner-smooth type-ui-xs cursor-pointer rounded-xl bg-transparent px-1.5 py-1.5 text-left font-normal transition-colors"
        >
          {isExpanded ? 'View less' : `View ${chats.length - MAX_COLLAPSED_CHATS} more`}
        </button>
      )}
    </div>
  )
}
